/**
 * Schedule Callback Tool
 * ElevenLabs webhook tool for scheduling follow-up calls during conversations
 * 
 * Use Cases:
 * - Debt Collection: Customer says "call me back in 2 days" → Schedule follow-up
 * - Appointment: Customer wants reminder call → Schedule callback
 * - Follow-up: Customer needs time to decide → Schedule check-in call
 */

import type { 
  CallbackToolRequest, 
  CallbackToolResponse, 
  ExecutionContext,
  CallbackReason,
} from '../types';

export interface ScheduleCallbackToolConfig {
  type: 'webhook';
  name: string;
  description: string;
  api_schema: {
    url: string;
    method: 'POST';
    headers: Record<string, string>;
    request_body_schema: {
      type: 'object';
      properties: Record<string, any>;
      required: string[];
    };
  };
}

/**
 * Get the schedule_callback webhook tool configuration for ElevenLabs
 */
export function getScheduleCallbackTool(domain: string, secret: string, agentId: string): ScheduleCallbackToolConfig {
  const webhookUrl = `${domain}/api/plugins/database-connector/tools/callback/${secret}/${agentId}`;

  return {
    type: 'webhook',
    name: 'schedule_callback',
    description: `Schedule a follow-up call with the customer at a later time. Use this when:
- Customer asks to be called back later ("اتصل بي بكره", "call me tomorrow")
- Customer needs time to check something ("أحتاج أسأل زوجتي أولاً")
- Customer requests reminder call ("ذكرني قبل الموعد")
- Payment reminder follow-up ("اتصل بي بعد يومين")
- Customer is busy now ("أنا مشغول الآن")

Time formats accepted:
- Relative: "in 2 days", "in 1 hour", "tomorrow", "بعد يومين", "بكره"
- Specific: "2024-01-15 10:00", "الأحد الساعة 10"`,
    api_schema: {
      url: webhookUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      request_body_schema: {
        type: 'object',
        properties: {
          scheduled_time: {
            type: 'string',
            description: 'When to call back. Can be relative (in 2 days, tomorrow) or specific (2024-01-15 10:00)',
          },
          reason: {
            type: 'string',
            enum: ['payment_reminder', 'follow_up', 'appointment_confirmation', 'survey', 'custom'],
            description: 'The reason for the callback',
          },
          notes: {
            type: 'string',
            description: 'Notes about what to discuss in the callback',
          },
          amount: {
            type: 'number',
            description: 'If payment reminder, the amount due',
          },
          invoice_id: {
            type: 'string',
            description: 'If payment reminder, the invoice ID',
          },
        },
        required: ['scheduled_time', 'reason'],
      },
    },
  };
}

/**
 * Handle the schedule_callback tool call
 */
export async function handleScheduleCallbackTool(
  request: CallbackToolRequest,
  context: ExecutionContext,
  callbackScheduler: any
): Promise<CallbackToolResponse> {
  console.log(`[Callback Tool] Scheduling callback for ${request.phoneNumber} at ${request.scheduledAt}`);

  try {
    // Validate phone number
    if (!request.phoneNumber) {
      // Use caller's phone if not specified
      if (!context.callerPhone) {
        return {
          success: false,
          message: 'لم يتم تحديد رقم الهاتف للاتصال.',
        };
      }
      request.phoneNumber = context.callerPhone;
    }

    // Schedule the callback
    const result = await callbackScheduler.scheduleCallbackFromAgent(
      context,
      context.agentId!,
      request.phoneNumber,
      request.scheduledAt,
      request.reason,
      request.reasonDetails,
      request.customerName,
      request.agentContext
    );

    if (result.success) {
      return {
        success: true,
        message: result.message,
        callbackId: result.callbackId,
        scheduledAt: result.scheduledAt?.toISOString(),
      };
    } else {
      return {
        success: false,
        message: result.message,
      };
    }
  } catch (error: any) {
    console.error('[Callback Tool] Error:', error);
    return {
      success: false,
      message: 'حدث خطأ أثناء جدولة المكالمة. يرجى المحاولة مرة أخرى.',
    };
  }
}

/**
 * Build context message for the callback based on current conversation
 */
export function buildCallbackContext(
  reason: CallbackReason,
  conversationSummary?: string,
  additionalNotes?: string
): string {
  let context = '';

  switch (reason) {
    case 'payment_reminder':
      context = `هذه مكالمة متابعة لتذكير العميل بالدفع. `;
      break;
    case 'follow_up':
      context = `هذه مكالمة متابعة. `;
      break;
    case 'appointment_confirmation':
      context = `هذه مكالمة لتأكيد الموعد. `;
      break;
    default:
      context = `هذه مكالمة متابعة مجدولة. `;
  }

  if (conversationSummary) {
    context += `\n\nملخص المحادثة السابقة:\n${conversationSummary}`;
  }

  if (additionalNotes) {
    context += `\n\nملاحظات إضافية:\n${additionalNotes}`;
  }

  return context;
}

/**
 * Validate callback request
 */
export function validateCallbackRequest(request: Partial<CallbackToolRequest>): {
  valid: boolean;
  error?: string;
} {
  if (!request.scheduledAt) {
    return { valid: false, error: 'يجب تحديد وقت المكالمة' };
  }

  if (!request.reason) {
    return { valid: false, error: 'يجب تحديد سبب المكالمة' };
  }

  const validReasons: CallbackReason[] = [
    'payment_reminder',
    'follow_up',
    'appointment_confirmation',
    'survey',
    'custom',
  ];

  if (!validReasons.includes(request.reason)) {
    return { valid: false, error: 'سبب المكالمة غير صالح' };
  }

  return { valid: true };
}
